import * as React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslations } from "next-intl";
import {
  Building2,
  Gift,
  KeyRound,
  LogOut,
  Ticket,
  UserCog,
} from "lucide-react";
import { Avatar, Badge, cn } from "@chotca/ui";
import { useLogout, useMe, usePlanStatus } from "@chotca/api-client";
import { useAuthStore } from "@chotca/auth";
import { EditProfileDialog } from "@/components/employer/edit-profile-dialog";
import { ChangePasswordDialog } from "@/components/employer/change-password-dialog";
import { ContactChannelsCard } from "@/components/employer/contact-channels-card";
import { useSubscriptionModals } from "@/components/employer/subscription-modals";
import { setAccessToken } from "@/boot/access-token";

export function ProfileClient() {
  const t = useTranslations("avatarMenu");
  const tNav = useTranslations("appNav");
  const nav = useNavigate();
  const me = useMe();
  const activeOrgId = useAuthStore((s) => s.activeOrgId);
  const plan = usePlanStatus(activeOrgId);
  const isFree = (plan.data?.effectiveCode ?? "FREE") === "FREE";
  const { openUpgrade, openTopup, openRedeemGiftCode } =
    useSubscriptionModals();
  const logout = useLogout({
    redirectTo: "/login",
    onCleared: () => setAccessToken(null),
  });
  const [editOpen, setEditOpen] = React.useState(false);
  const [pwOpen, setPwOpen] = React.useState(false);

  if (!me.data) return null;
  const user = me.data;
  const ep = user.employerProfile;
  const displayName = ep?.fullName?.trim() || user.email || "User";
  const orgRole =
    (user.memberships.find((m) => m.orgId === activeOrgId)?.role as
      | "OWNER"
      | "HR"
      | undefined) ??
    (user.memberships[0]?.role as "OWNER" | "HR" | undefined) ??
    null;
  const roleLabel =
    orgRole === "OWNER"
      ? t("roleOwner")
      : orgRole === "HR"
        ? t("roleHr")
        : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4 rounded-2xl border border-smoke bg-paper p-4">
        <Avatar
          name={displayName}
          src={ep?.avatarUrl ?? undefined}
          size="lg"
          tone="ink"
        />
        <div className="min-w-0 flex-1">
          <p className="truncate text-title-md font-extrabold text-ink">
            {displayName}
          </p>
          {user.email ? (
            <p className="truncate text-xs text-ash">{user.email}</p>
          ) : null}
          {roleLabel ? (
            <div className="mt-1.5">
              <Badge tone={orgRole === "OWNER" ? "lime-soft" : "mist"}>
                {roleLabel}
              </Badge>
            </div>
          ) : null}
        </div>
      </div>

      <ContactChannelsCard user={user} />

      <div className="overflow-hidden rounded-2xl border border-smoke bg-paper">
        <Row
          icon={<Building2 size={18} strokeWidth={1.7} />}
          label={t("orgInfo")}
          onClick={() => nav("/org")}
        />
        <Row
          icon={<Ticket size={18} strokeWidth={1.7} />}
          label={isFree ? t("upgradePlan") : t("buySourcing")}
          onClick={() => (isFree ? openUpgrade() : openTopup("SOURCING"))}
        />
        <Row
          icon={<Gift size={18} strokeWidth={1.7} />}
          label={t("redeemGiftCode")}
          onClick={openRedeemGiftCode}
        />
        <Row
          icon={<UserCog size={18} strokeWidth={1.7} />}
          label={t("editProfile")}
          onClick={() => setEditOpen(true)}
        />
        <Row
          icon={<KeyRound size={18} strokeWidth={1.7} />}
          label={t("changePassword")}
          onClick={() => setPwOpen(true)}
        />
      </div>

      <div className="overflow-hidden rounded-2xl border border-smoke bg-paper">
        <Row
          icon={<LogOut size={18} strokeWidth={1.7} />}
          label={tNav("logout")}
          onClick={() => {
            void logout();
          }}
          tone="danger"
        />
      </div>

      <EditProfileDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        user={user}
      />
      <ChangePasswordDialog
        open={pwOpen}
        onClose={() => setPwOpen(false)}
        email={user.email}
      />
    </div>
  );
}

function Row({
  icon,
  label,
  onClick,
  tone,
  disabled,
}: {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  tone?: "danger";
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={cn(
        "flex w-full items-center gap-3 px-4 py-3.5 text-left text-sm font-semibold transition-colors not-last:border-b not-last:border-smoke disabled:opacity-60",
        tone === "danger"
          ? "text-danger hover:bg-danger/10"
          : "text-ink hover:bg-mist",
      )}
    >
      <span
        className={cn(
          "flex h-9 w-9 items-center justify-center rounded-full",
          tone === "danger" ? "bg-danger/10" : "bg-mist",
        )}
      >
        {icon}
      </span>
      <span className="flex-1 truncate">{label}</span>
    </button>
  );
}
